import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

/**
 * Formats all suppliers into a single string separated by line breaks
 * @param {Array} suppliers - Array of supplier objects
 * @returns {string} Formatted supplier string
 */
const formatSuppliers = (suppliers) => {
    if (!suppliers || suppliers.length === 0) return 'Nenhum cadastrado';

    return suppliers
        .map(s => s.razaoSocial || 'N/A')
        .join('\n');
};

/**
 * Exports a production summary to Excel with MP and EMB sections
 * @param {Object} params
 * @param {Object} params.globalValidation - Global validation totals
 * @param {Array} params.allRawMaterials - All raw materials data
 * @param {Array} params.allCategories - All categories data
 * @param {Array} params.products - Products data
 * @param {string} [params.filePrefix='Resumo_OP'] - File name prefix
 */
export const exportProductionSummary = async ({
    globalValidation,
    allRawMaterials,
    allCategories,
    products,
    filePrefix = 'Resumo_OP'
}) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Resumo de Produção');

    worksheet.columns = [
        { header: 'Cód. MP', key: 'code', width: 12 },
        { header: 'Nome Comercial', key: 'name', width: 80 },
        { header: 'INCI Name', key: 'inci', width: 80 },
        { header: 'Categoria', key: 'category', width: 25 },
        { header: 'Quantidade Necessária', key: 'needed', width: 35 },
        { header: 'Quantidade Faltante', key: 'missing', width: 20 },
        { header: 'Unid.', key: 'unit', width: 8 },
        { header: 'Valor Unitário', key: 'unitPrice', width: 15 },
        { header: 'Valor Total da Quantidade Total', key: 'totalPriceTotal', width: 35 },
        { header: 'Valor Total da Quantidade Faltante', key: 'totalPriceMissing', width: 35 },
        { header: 'Fornecedor', key: 'supplier', width: 80 },
        { header: 'Tipo', key: 'type', width: 20 },
    ];

    const unitCategoryIds = allCategories
        .filter(c => ['SERIGRAFIA', 'EMBALAGEM'].includes(c.name?.toUpperCase()))
        .map(c => c.id);

    const mpItems = [];
    const embItems = [];

    Object.values(globalValidation.totals).forEach(t => {
        const rmData = allRawMaterials.find(rm => rm.id === t.id);
        const isUnit = rmData?.categoryIds?.some(id => unitCategoryIds.includes(id));
        if (isUnit) {
            embItems.push(t);
        } else {
            mpItems.push(t);
        }
    });

    const addDataRows = (items, sectionType) => {
        let sumTotalMissing = 0;
        let sumTotalTotal = 0;

        items.forEach(t => {
            const rmData = allRawMaterials.find(rm => rm.id === t.id);
            const productData = products.find(p => p.id === t.id);
            const dataSource = rmData || productData;

            let categoryNames = [];
            if (rmData?.categoryIds) {
                categoryNames = rmData.categoryIds
                    .map(catId => {
                        const c = allCategories.find(c => c.id === catId);
                        return c ? c.name : '';
                    })
                    .filter(Boolean);
            } else if (productData?.categoryIds) {
                categoryNames = productData.categoryIds
                    .map(catId => {
                        const c = allCategories.find(c => c.id === catId);
                        return c ? c.name : '';
                    })
                    .filter(Boolean);
            }

            let uPrice = 0;
            let supplierName = 'Nenhum cadastrado';

            if (productData) {
                uPrice = parseFloat(productData.totalFormulationCost) || 0;
                supplierName = 'Produção Interna (Semi Acabado)';
            } else if (rmData) {
                uPrice = parseFloat(rmData.suppliers?.[0]?.valor) || 0;
                supplierName = formatSuppliers(rmData.suppliers);
            }

            const stockActual = t.isWater ? t.needed : (t.stock || 0);
            const difference = Math.max(0, t.needed - stockActual);
            const totalMissing = difference * uPrice;
            const totalTotal = t.needed * uPrice;

            sumTotalMissing += totalMissing;
            sumTotalTotal += totalTotal;

            worksheet.addRow({
                code: dataSource?.code || '—',
                name: dataSource?.commercialName || '—',
                inci: rmData?.inciName || productData?.commercialName || '—',
                category: categoryNames.join(', ') || 'Sem categoria',
                needed: Number(t.needed.toFixed(3)),
                unit: t.unit,
                missing: Number(difference.toFixed(3)),
                unitPrice: uPrice,
                totalPriceMissing: Number(totalMissing.toFixed(2)),
                totalPriceTotal: Number(totalTotal.toFixed(2)),
                supplier: supplierName,
                type: sectionType,
            });
        });

        return { sumTotalMissing, sumTotalTotal };
    };

    // MP Section
    if (mpItems.length > 0) {
        const headerRow = worksheet.addRow({
            code: 'MATÉRIAS-PRIMAS', name: '', inci: '', category: '', needed: '',
            unit: '', missing: '', unitPrice: '', totalPriceMissing: '',
            totalPriceTotal: '', supplier: '', type: 'MP_HEADER',
        });
        headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E4057' } };
        headerRow.alignment = { horizontal: 'left', vertical: 'middle' };
        headerRow.height = 25;
        worksheet.mergeCells(`A${headerRow.number}:L${headerRow.number}`);

        const mpTotals = addDataRows(mpItems, 'Matéria-Prima');

        if (mpItems.length > 1) {
            const subtotalRow = worksheet.addRow({
                code: '', name: '', inci: '', category: '', needed: '',
                unit: '', missing: '', unitPrice: 'SUBTOTAL MP:',
                totalPriceMissing: Number(mpTotals.sumTotalMissing.toFixed(2)),
                totalPriceTotal: Number(mpTotals.sumTotalTotal.toFixed(2)),
                supplier: '', type: 'MP_SUBTOTAL',
            });
            subtotalRow.font = { bold: true, color: { argb: 'FF1A3A5C' } };
            subtotalRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EDF2' } };
        }
    }

    // EMB Section
    if (embItems.length > 0) {
        worksheet.addRow({
            code: '', name: '', inci: '', category: '', needed: '',
            unit: '', missing: '', unitPrice: '', totalPriceMissing: '',
            totalPriceTotal: '', supplier: '', type: 'EMPTY'
        });

        const headerRow = worksheet.addRow({
            code: 'EMBALAGENS E SERIGRAFIA', name: '', inci: '', category: '', needed: '',
            unit: '', missing: '', unitPrice: '', totalPriceMissing: '',
            totalPriceTotal: '', supplier: '', type: 'EMB_HEADER',
        });
        headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E4057' } };
        headerRow.alignment = { horizontal: 'left', vertical: 'middle' };
        headerRow.height = 25;
        worksheet.mergeCells(`A${headerRow.number}:L${headerRow.number}`);

        const embTotals = addDataRows(embItems, 'Embalagem/Serigrafia');

        if (embItems.length > 1) {
            const subtotalRow = worksheet.addRow({
                code: '', name: '', inci: '', category: '', needed: '',
                unit: '', missing: '', unitPrice: 'SUBTOTAL EMB:',
                totalPriceMissing: Number(embTotals.sumTotalMissing.toFixed(2)),
                totalPriceTotal: Number(embTotals.sumTotalTotal.toFixed(2)),
                supplier: '', type: 'EMB_SUBTOTAL',
            });
            subtotalRow.font = { bold: true, color: { argb: 'FF1A3A5C' } };
            subtotalRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EDF2' } };
        }
    }

    // Grand Total
    const allItems = [...mpItems, ...embItems];
    if (allItems.length > 0) {
        let grandTotalMissing = 0;
        let grandTotalTotal = 0;

        allItems.forEach(t => {
            const rmData = allRawMaterials.find(rm => rm.id === t.id);
            const productData = products.find(p => p.id === t.id);
            let uPrice = 0;
            if (productData) {
                uPrice = parseFloat(productData.totalFormulationCost) || 0;
            } else if (rmData) {
                uPrice = parseFloat(rmData.suppliers?.[0]?.valor) || 0;
            }
            const stockActual = t.isWater ? t.needed : (t.stock || 0);
            const difference = Math.max(0, t.needed - stockActual);
            grandTotalMissing += difference * uPrice;
            grandTotalTotal += t.needed * uPrice;
        });

        worksheet.addRow({
            code: '', name: '', inci: '', category: '', needed: '',
            unit: '', missing: '', unitPrice: '', totalPriceMissing: '',
            totalPriceTotal: '', supplier: '', type: 'EMPTY'
        });

        const totalRow = worksheet.addRow({
            code: '', name: '', inci: '', category: '', needed: '',
            unit: '', missing: '', unitPrice: 'TOTAL GERAL:',
            totalPriceMissing: Number(grandTotalMissing.toFixed(2)),
            totalPriceTotal: Number(grandTotalTotal.toFixed(2)),
            supplier: '', type: 'GRAND_TOTAL',
        });
        totalRow.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
        totalRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A3A5C' } };
        totalRow.height = 30;
    }

    // Formatting
    const currencyFormat = '"R$ " #,##0.00';

    worksheet.eachRow((row, rowNumber) => {
        const cellValue = row.getCell(1).value;
        const typeValue = row.getCell('type')?.value;
        const isSectionHeader = cellValue === 'MATÉRIAS-PRIMAS' || cellValue === 'EMBALAGENS E SERIGRAFIA';
        const isSpecialRow = isSectionHeader ||
            typeValue === 'MP_SUBTOTAL' || typeValue === 'EMB_SUBTOTAL' ||
            typeValue === 'GRAND_TOTAL' || typeValue === 'EMPTY';

        if (!isSpecialRow) {
            row.eachCell({ includeEmpty: true }, (cell) => {
                const isSupplierColumn = cell.col === 11; // Coluna K (Fornecedor)
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: isSupplierColumn ? 'left' : 'center',
                    wrapText: true
                };
                cell.border = {
                    top: { style: 'thin' }, left: { style: 'thin' },
                    bottom: { style: 'thin' }, right: { style: 'thin' }
                };
            });

            if (rowNumber === 1) {
                row.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A3A5C' } };
            } else {
                if (row.getCell('unitPrice').value && typeof row.getCell('unitPrice').value === 'number') {
                    row.getCell('unitPrice').numFmt = currencyFormat;
                    row.getCell('totalPriceMissing').numFmt = currencyFormat;
                    row.getCell('totalPriceTotal').numFmt = currencyFormat;
                }
                if (rowNumber % 2 === 0) {
                    row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
                }
            }
        }
    });

    // Ajustar altura das linhas para acomodar múltiplos fornecedores
    worksheet.eachRow((row, rowNumber) => {
        const supplierCell = row.getCell('supplier');
        if (supplierCell.value && typeof supplierCell.value === 'string' && supplierCell.value.includes('\n')) {
            const lineCount = supplierCell.value.split('\n').length;
            row.height = Math.max(row.height || 15, lineCount * 15);
        }
    });

    const lastRow = worksheet.rowCount;
    const lastCol = worksheet.columnCount;
    worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: lastRow, column: lastCol - 1 }
    };
    const typeColumn = worksheet.getColumn('type');
    if (typeColumn) typeColumn.hidden = true;

    worksheet.protect('', {
        autoFilter: true, sort: true,
        selectLockedCells: true, selectUnlockedCells: true
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${filePrefix}_${new Date().getTime()}.xlsx`);
};